import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();
const THEME_COOKIE = 'blog-theme';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function readStoredTheme() {
    // Cookies are shared between Safari and Home Screen (standalone) apps on iOS,
    // while localStorage is NOT. Read cookie first, fall back to localStorage.
    try {
        const m = document.cookie.match(/(?:^|;\s*)blog-theme=([^;]*)/);
        if (m && m[1]) return m[1];
    } catch { /* ignore */ }
    try {
        const t = localStorage.getItem('blog-theme');
        if (t) return t;
    } catch { /* ignore */ }
    return null;
}

function persistTheme(theme) {
    try {
        document.cookie = `${THEME_COOKIE}=${encodeURIComponent(theme)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
    } catch { /* ignore */ }
    try {
        localStorage.setItem('blog-theme', theme);
    } catch { /* ignore */ }
}

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        const stored = readStoredTheme();
        if (stored) return stored;
        return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    });

    useEffect(() => {
        // Apply theme class to body
        document.body.classList.remove('light-theme', 'dark-theme');
        document.body.classList.add(`${theme}-theme`);
        // Keep the iOS status bar / browser chrome tint in sync
        const meta = document.querySelector('meta[name="theme-color"]');
        if (meta) {
            meta.setAttribute('content', theme === 'light' ? '#fcfcfc' : '#000000');
        }
    }, [theme]);

    // Always live-follow the OS theme (like Facebook / Messenger).
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: light)');
        const onChange = () => setTheme(mq.matches ? 'light' : 'dark');
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const toggleTheme = () => {
        setTheme((prevTheme) => {
            const next = prevTheme === 'light' ? 'dark' : 'light';
            persistTheme(next);
            return next;
        });
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
